import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import {
  getGoogleOAuthRedirectUri,
  getMicrosoftOAuthRedirectUri,
} from "../utils/oauthRedirectUris";

const LOGIN_CONTEXT_TOKEN_KEY = "login_context_token";
const LOGIN_ORG_NAME_KEY = "login_organization_name";

export function getLoginContextToken(): string | null {
  return sessionStorage.getItem(LOGIN_CONTEXT_TOKEN_KEY);
}

export function setLoginSession(
  contextToken: string,
  organizationName: string,
) {
  sessionStorage.setItem(LOGIN_CONTEXT_TOKEN_KEY, contextToken);
  sessionStorage.setItem(LOGIN_ORG_NAME_KEY, organizationName);
  sessionStorage.setItem("microsoft_oauth_context_token", contextToken);
}

export function clearLoginSession() {
  sessionStorage.removeItem(LOGIN_CONTEXT_TOKEN_KEY);
  sessionStorage.removeItem(LOGIN_ORG_NAME_KEY);
  sessionStorage.removeItem("microsoft_oauth_context_token");
}

export default function LoginProviders() {
  const navigate = useNavigate();
  const contextToken = getLoginContextToken();
  const organizationName =
    sessionStorage.getItem(LOGIN_ORG_NAME_KEY) ?? "your organization";

  useEffect(() => {
    if (!contextToken) {
      navigate("/login", { replace: true });
    }
  }, [contextToken, navigate]);

  const handleGoogleLogin = async () => {
    try {
      const response = await apiClient.get(`/auth/google/authorize`, {
        params: { redirect_uri: getGoogleOAuthRedirectUri() },
      });
      sessionStorage.setItem("oauth_provider", "google");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Failed to get Google auth URL:", error);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!contextToken) return;
    try {
      const response = await apiClient.get(`/auth/microsoft/authorize`, {
        params: {
          redirect_uri: getMicrosoftOAuthRedirectUri(),
          context_token: contextToken,
        },
      });
      sessionStorage.setItem("oauth_provider", "microsoft");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Failed to get Microsoft auth URL:", error);
    }
  };

  if (!contextToken) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-emerald-50 via-teal-50 to-cyan-50 px-4 py-8">
      <div className="w-2/3 lg:w-1/2 sm:w-full max-w-lg">
        <div className="relative bg-white rounded-2xl shadow-xl border-2 border-emerald-200 p-6 md:p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="p-3 mb-4">
              <img
                src="/logo.jpeg"
                className="rounded-xl object-cover h-20 w-56"
                alt="Datamint logo"
              />
            </div>
            <h1 className="font-bold bg-linear-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent text-2xl">
              Choose sign-in method
            </h1>
            <p className="text-slate-600 font-medium text-sm mt-1 text-center">
              Continue to {organizationName}
            </p>
          </div>

          <div className="w-full space-y-4">
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-700 font-medium py-2.5 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <img src="/google.svg" alt="Google" className="h-5 w-5" />
              Continue with Google
            </button>

            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-teal-300 hover:border-teal-400 hover:bg-teal-50 text-teal-700 font-medium py-2.5 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <img src="/microsoft.svg" alt="Microsoft" className="h-5 w-5" />
              Continue with Microsoft
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              clearLoginSession();
              navigate("/login", { replace: true });
            }}
            className="mt-6 w-full text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Use a different email
          </button>
        </div>
      </div>
    </div>
  );
}
